import { gettext } from '@eventbrite/i18n';
import { GenericLazyString } from 'packages/infra/i18n/src';
import React from 'react';
import './BreadCrumbs.scss';

export type BreadCrumbItem<T> = T & {
    display: React.ReactElement | string | GenericLazyString;
    value: string;
};

export interface BreadCrumbsProps<T> {
    /**
     * Array of BreadCrumbItem objects that
     * define display and value for the
     * particular BreadCrumb
     */
    items: BreadCrumbItem<T>[];
    /**
     * To use the component the item is passed back to
     * the caller via the renderProp pattern.
     *
     * The consumer can then define whatever wrapper they
     * prefer.
     */
    children: (item: BreadCrumbItem<T>) => React.ReactElement;
    /**
     * Classes to be applied to the wrapping container
     */
    containerClasses?: string;
    /**
     * Node to be used to separate the breadcrumb
     * elements.
     */
    separator?: React.ReactElement | string | GenericLazyString;
}

export function BreadCrumbs<T>(props: BreadCrumbsProps<T>) {
    const { items, containerClasses, separator = '/' } = props;

    return (
        <nav
            aria-label={gettext('breadcrumbs').toString()}
            className="discover-breadcrumbs"
        >
            <ol className={containerClasses}>
                {items.map((item, index) => {
                    const renderSeparator =
                        items.length > 1 && index < items.length - 1;
                    const isLast = index === items.length - 1;

                    return (
                        <li key={item.value} data-testid="breadcrumb-element">
                            <>
                                {isLast ? (
                                    <span
                                        aria-current="page"
                                        data-testid="breadcrumb-final-element"
                                    >
                                        {item.display}
                                    </span>
                                ) : (
                                    props.children(item)
                                )}
                                {renderSeparator ? (
                                    <span
                                        data-testid="breadcrumb-separator"
                                        aria-hidden="true"
                                    >
                                        {separator}
                                    </span>
                                ) : null}
                            </>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
